import { useNavigate } from "react-router-dom";
// src/pages/LogsPage.jsx — v8.8 FINAL
//
// ПАТТЕРН: Datadog Log Management + Grafana Loki
// "Под капотом всё под контролем — вот доказательства"
//
// КЛЮЧЕВЫЕ РЕШЕНИЯ:
// 1. Header: "Activity Log" + мини-stats (engine events / warnings)
// 2. Heartbeat/шумовые логи: opacity 0.35 — не мешают, но видны
// 3. Engine events (antiloss, cron_risk): лёгкий синий фон — выделяются
// 4. WARNING/ERROR: полный контраст
// 5. Группировка визуальная: важные всплывают наверх при сортировке
// 6. Пустое состояние: "Engine running silently"

import { useState, useMemo, useEffect } from "react";
import { workerClient } from "../api/workerClient";
import LogRow from "../components/ui/LogRow";

const selectStyle = {
  background: "var(--bg-tertiary)", border: "1px solid var(--border-subtle)",
  borderRadius: "var(--radius)", padding: "6px 10px", fontSize: 12,
  color: "var(--text-primary)", outline: "none", cursor: "pointer",
  fontFamily: "var(--font-mono)",
};

const labelStyle = {
  fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.14em",
  textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 5, display: "block",
};

function isNoise(log) {
  const msg = (log.message ?? "").toLowerCase();
  const src = (log.source ?? "").toLowerCase();
  return msg.includes("heartbeat") || src.includes("heartbeat");
}

function isEngineEvent(log) {
  const src = (log.source ?? "").toLowerCase();
  const msg = (log.message ?? "").toLowerCase();
  const lvl = (log.level ?? "").toLowerCase();
  return lvl === "error" || lvl === "warning"
    || src.includes("antiloss") || src.includes("cron_risk")
    || msg.includes("action_created") || msg.includes("recovery")
    || msg.includes("critical") || msg.includes("risk_engine");
}

export default function LogsPage() {
  const navigate = useNavigate();
  const [logs,        setLogs]        = useState([]);
  const [error,       setError]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [levelFilter, setLevelFilter] = useState("all");
  const [sortOrder,   setSortOrder]   = useState("desc");

  useEffect(() => {
    workerClient("/api/logs")
      .then(res => setLogs(Array.isArray(res?.logs) ? res.logs : []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = [...logs];
    if (levelFilter !== "all") {
      list = list.filter(l => (l.level ?? "info").toLowerCase() === levelFilter);
    }
    list.sort((a, b) =>
      sortOrder === "desc"
        ? (b.timestamp ?? 0) - (a.timestamp ?? 0)
        : (a.timestamp ?? 0) - (b.timestamp ?? 0)
    );
    return list;
  }, [logs, levelFilter, sortOrder]);

  const stats = useMemo(() => ({
    engine:   logs.filter(l => isEngineEvent(l) && !isNoise(l)).length,
    warnings: logs.filter(l => (l.level ?? "").toLowerCase() === "warning").length,
    errors:   logs.filter(l => (l.level ?? "").toLowerCase() === "error").length,
  }), [logs]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Return to Analyze */}
      <div data-no-print style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
        <button
          onClick={() => navigate("/analyze")}
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, cursor: "pointer",
            padding: "4px 10px", borderRadius: "var(--radius-sm)",
            background: "transparent", border: "1px solid var(--border-subtle)",
            color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 5,
          }}
        >
          ← Analyze
        </button>
      </div>

      {/* ── TRANSPARENCY HEADER ── */}
      <div style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16,
        paddingBottom: 14, borderBottom: "1px solid var(--border-subtle)",
      }}>
        <div>
          <div style={{
            fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
            letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--text-secondary)",
            marginBottom: 4,
          }}>
            System Transparency
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-muted)", letterSpacing: "0.04em" }}>
            System events · import history · risk checks
          </div>
        </div>

        {/* Мини-статистика */}
        {!loading && (
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexShrink: 0 }}>
            {stats.engine > 0 && (
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--accent-primary)" }}>
                {stats.engine} engine events
              </div>
            )}
            {stats.warnings > 0 && (
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--warning)" }}>
                {stats.warnings} warnings
              </div>
            )}
            {stats.errors > 0 && (
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--danger)" }}>
                {stats.errors} errors
              </div>
            )}
            {stats.engine === 0 && stats.warnings === 0 && stats.errors === 0 && (
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--success)" }}>
                ✓ All clean
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── FILTERS ── */}
      <div style={{
        display: "flex", alignItems: "flex-end", gap: 20,
        padding: "10px 14px",
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius)",
      }}>
        <div>
          <span style={labelStyle}>Level</span>
          <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)} style={selectStyle}>
            <option value="all">All</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
        </div>
        <div>
          <span style={labelStyle}>Sort</span>
          <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} style={selectStyle}>
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
            {loading ? "…" : `${filtered.length} log${filtered.length !== 1 ? "s" : ""}`}
          </span>
        </div>
      </div>

      {/* ── LIST ── */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius)", overflow: "hidden",
      }}>
        {loading ? (
          <div style={{ padding: "20px 16px", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: "20px 16px", fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--danger)" }}>Error: {error}</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "36px 16px", textAlign: "center" }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--success)", marginBottom: 8 }}>
              ✓ No log entries for this period
            </div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
              No log entries for selected filter
            </div>
          </div>
        ) : (
          filtered.map((item, i) => {
            const noise  = isNoise(item);
            const engine = isEngineEvent(item);
            return (
              <div key={item.id ?? i} style={{
                borderBottom: i < filtered.length - 1 ? "1px solid var(--border-subtle)" : "none",
                // Noise: почти невидимый. Engine events: лёгкий акцент.
                opacity:    noise ? 0.35 : 1,
                background: engine && !noise ? "rgba(60,130,230,0.025)" : "transparent",
              }}>
                <LogRow item={item} />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}


